import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { RadarObject } from '../types';
import { RADAR_COLORS } from '../constants/rankColors';

interface SkillRadarChartProps {
  /** 5軸 + Lv + is_provisional（付録D.1 RadarObject） */
  data: RadarObject;
  /** チャート一辺サイズ(px)。既定 240 */
  size?: number;
  /** 軸ラベルを表示するか（既定 true） */
  showLabels?: boolean;
}

// 軸順（時計回り・上頂点起点）: 対人 → 知識 → 統率 → 規律 → 実行
const AXES: { key: keyof RadarObject; label: string }[] = [
  { key: 'interpersonal', label: '対人' },
  { key: 'knowledge',     label: '知識' },
  { key: 'leadership',   label: '統率' },
  { key: 'discipline',   label: '規律' },
  { key: 'execution',    label: '実行' },
];

const MAX_VALUE = 100;
const RINGS = [0.2, 0.4, 0.6, 0.8, 1]; // グリッド同心リング

/**
 * 自前 SVG 5軸レーダーチャート（チャートライブラリ未導入のため §12-6）。
 * 軸順は（対人 → 知識 → 統率 → 規律 → 実行）を上頂点起点・時計回りで描画。
 * is_provisional=true（判定 < 3 シフト）の場合は全軸55の暫定円を破線+薄色で表現し
 * 「暫定（評価データ不足）」を明示する。
 */
const SkillRadarChart: React.FC<SkillRadarChartProps> = ({ data, size = 240, showLabels = true }) => {
  const cx = size / 2;
  const cy = size / 2;
  // ラベル余白を確保した実半径
  const radius = (size / 2) * (showLabels ? 0.66 : 0.82);

  // 上頂点起点・時計回り。i 番目の軸角度（ラジアン）
  const angleOf = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / AXES.length;

  // 値(0-100) → 座標
  const pointAt = (i: number, value: number) => {
    const r = (Math.max(0, Math.min(MAX_VALUE, value)) / MAX_VALUE) * radius;
    const a = angleOf(i);
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  };

  const isProvisional = data.is_provisional;

  // 暫定時は全軸55の円、通常は実値で多角形
  const values = AXES.map((axis) => (isProvisional ? 55 : (data[axis.key] as number)));
  const polygonPoints = values
    .map((v, i) => {
      const p = pointAt(i, v);
      return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    })
    .join(' ');

  // 軸線の終端（max=100）
  const axisEnds = AXES.map((_, i) => pointAt(i, MAX_VALUE));

  // 同心リングの多角形
  const ringPolygons = RINGS.map((ratio) =>
    AXES.map((_, i) => {
      const p = pointAt(i, MAX_VALUE * ratio);
      return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    }).join(' '),
  );

  // ラベル座標（外周のさらに外側）
  const labelPos = AXES.map((_, i) => {
    const a = angleOf(i);
    const lr = radius + (showLabels ? size * 0.05 : 0);
    return { x: cx + lr * Math.cos(a), y: cy + lr * Math.sin(a) };
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="スキルパラメーター">
        {/* グリッド（同心リング） */}
        {ringPolygons.map((pts, idx) => (
          <polygon
            key={`ring-${idx}`}
            points={pts}
            fill="none"
            stroke={RADAR_COLORS.grid}
            strokeWidth={1}
          />
        ))}
        {/* 軸線 */}
        {axisEnds.map((p, i) => (
          <line key={`axis-${i}`} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={RADAR_COLORS.grid} strokeWidth={1} />
        ))}
        {/* データ多角形（暫定時は破線・薄塗り） */}
        <polygon
          points={polygonPoints}
          fill={RADAR_COLORS.fill}
          fillOpacity={isProvisional ? 0.4 : 0.75}
          stroke={RADAR_COLORS.stroke}
          strokeWidth={2}
          strokeOpacity={isProvisional ? 0.6 : 1}
        />
        {/* 頂点ドット（通常時のみ） */}
        {!isProvisional &&
          values.map((v, i) => {
            const p = pointAt(i, v);
            return <circle key={`dot-${i}`} cx={p.x} cy={p.y} r={2.5} fill={RADAR_COLORS.stroke} />;
          })}
        {/* 軸ラベル */}
        {showLabels &&
          AXES.map((axis, i) => (
            <text
              key={`label-${i}`}
              x={labelPos[i].x}
              y={labelPos[i].y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={13}
              fontWeight={400}
              fill="#3c3c3c"
            >
              {axis.label}
            </text>
          ))}
      </svg>
      {isProvisional && (
        <Typography sx={{ fontSize: '11px', color: '#9e9e9e', mt: -2 }}>
          暫定表示（評価データが少ないため）
        </Typography>
      )}
    </Box>
  );
};

export default SkillRadarChart;
